import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Axios from 'axios'
import MUIDataTable from 'mui-datatables'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb } from 'app/components'
import { Box, styled } from '@mui/system'
import {
    Alert,
    Avatar,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RemoveShoppingCartRoundedIcon from '@mui/icons-material/RemoveShoppingCartRounded'
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import { LoadingButton } from '@mui/lab'
import { API_URI } from 'config'
import { formatStoreLabel } from 'app/views/settings/Account-Settings/AuthorizeAdmins'
import { notifyCartUpdated } from 'app/utils/cartEvents'

const Container = styled('div')(({ theme }) => ({
    margin: '30px',
    [theme.breakpoints.down('sm')]: { margin: '16px' },
    '& .breadcrumb': {
        marginBottom: '30px',
        [theme.breakpoints.down('sm')]: { marginBottom: '16px' },
    },
}))

/** Carts API may wrap the list in a few different shapes — pull out the array. */
function normalizeCartsResponse(data) {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.carts)) return data.carts
    if (Array.isArray(data?.cart)) return data.cart
    if (Array.isArray(data?.data)) return data.data
    if (Array.isArray(data?.data?.carts)) return data.data.carts
    if (Array.isArray(data?.items)) return data.items
    return []
}

function memberFullName(memberData) {
    const m = memberData || {}
    return (
        [m.firstName, m.lastName].filter(Boolean).join(' ') ||
        m.preferredName ||
        'Unnamed member'
    )
}

const CartViewer = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [carts, setCarts] = useState([])
    const [removeTarget, setRemoveTarget] = useState(null)
    const [removeLoading, setRemoveLoading] = useState(false)
    const [selectedStore, setSelectedStore] = useState('')
    const [checkoutLoading, setCheckoutLoading] = useState(false)
    const [checkoutConfirmOpen, setCheckoutConfirmOpen] = useState(false)
    const [toast, setToast] = useState({
        open: false,
        message: '',
        success: false,
    })

    const fetchCarts = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const { data } = await Axios.get(`${API_URI}/api/v1/carts/list`)
            setCarts(normalizeCartsResponse(data))
        } catch (e) {
            setError(
                e?.response?.data?.message ??
                    e?.message ??
                    'Could not load the cart.'
            )
            setCarts([])
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchCarts()
    }, [fetchCarts])

    /** Locations are derived from the stores present in the cart data (unique). */
    const storeOptions = useMemo(() => {
        const byId = new Map()
        carts.forEach((cart) => {
            const store = cart?.storeId
            const id = typeof store === 'string' ? store : store?._id
            if (id && !byId.has(id)) {
                byId.set(
                    id,
                    typeof store === 'object' ? store : { _id: id, name: id }
                )
            }
        })
        return Array.from(byId.values())
    }, [carts])

    useEffect(() => {
        if (!selectedStore && storeOptions.length === 1) {
            const onlyId = storeOptions[0]?._id
            if (onlyId) setSelectedStore(onlyId)
        }
    }, [storeOptions, selectedStore])

    const rows = useMemo(() => {
        if (!selectedStore) return carts
        return carts.filter((cart) => {
            const store = cart?.storeId
            const id = typeof store === 'string' ? store : store?._id
            return id === selectedStore
        })
    }, [carts, selectedStore])

    const selectedStoreObj = useMemo(
        () => storeOptions.find((s) => s?._id === selectedStore) || null,
        [storeOptions, selectedStore]
    )

    const selectedStoreItemCount = rows.length

    const handleOpenCheckoutConfirm = useCallback(() => {
        if (!selectedStore || selectedStoreItemCount === 0) return
        setCheckoutConfirmOpen(true)
    }, [selectedStore, selectedStoreItemCount])

    const handleCloseCheckoutConfirm = useCallback(() => {
        if (checkoutLoading) return
        setCheckoutConfirmOpen(false)
    }, [checkoutLoading])

    const handleConfirmCheckout = useCallback(async () => {
        if (!selectedStore) return
        setCheckoutLoading(true)
        try {
            const { data } = await Axios.post(
                `${API_URI}/api/v1/cart/continue`,
                { storeId: selectedStore }
            )
            const orderId = data?.orderId || data?._id || data?.order?._id
            setCheckoutConfirmOpen(false)
            notifyCartUpdated()
            if (orderId) {
                navigate(`/pages/order/${orderId}`)
                return
            }
            setToast({
                open: true,
                success: true,
                message: data?.message || 'Checkout started.',
            })
            await fetchCarts()
        } catch (e) {
            setToast({
                open: true,
                success: false,
                message:
                    e?.response?.data?.message ??
                    e?.message ??
                    'Could not continue to checkout for this location.',
            })
        } finally {
            setCheckoutLoading(false)
        }
    }, [selectedStore, navigate, fetchCarts])

    const handleCloseRemoveConfirm = useCallback(() => {
        if (removeLoading) return
        setRemoveTarget(null)
    }, [removeLoading])

    const executeRemove = useCallback(async () => {
        const cartItemId = removeTarget?._id
        if (!cartItemId) return
        setRemoveLoading(true)
        try {
            await Axios.delete(
                `${API_URI}/api/v1/cart/remove/${encodeURIComponent(cartItemId)}`
            )
            setRemoveTarget(null)
            setToast({
                open: true,
                success: true,
                message: 'Item removed from the cart.',
            })
            await fetchCarts()
            notifyCartUpdated()
        } catch (e) {
            setToast({
                open: true,
                success: false,
                message:
                    e?.response?.data?.message ??
                    e?.message ??
                    'Could not remove this item from the cart.',
            })
        } finally {
            setRemoveLoading(false)
        }
    }, [removeTarget, fetchCarts])

    const columns = useMemo(
        () => [
            {
                name: 'photo',
                label: 'Photo',
                options: {
                    filter: false,
                    customBodyRenderLite: (dataIndex) => {
                        const m = rows[dataIndex]?.memberData || {}

                        return (
                            <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                            >
                                <Avatar
                                    src={m.photo}
                                    alt={m.name || 'Unnamed member'}
                                    sx={{ width: 40, height: 40 }}
                                ></Avatar>
                            </Stack>
                        )
                    },
                },
            },
            {
                name: 'memberName',
                label: 'Name',
                options: {
                    filter: false,
                    customBodyRenderLite: (dataIndex) => {
                        const row = rows[dataIndex]
                        return row?.memberData?.firstName + ' ' + row?.memberData?.lastName || '-'
                    },
                },
            },
            {
                name: 'location',
                label: 'Location',
                options: {
                    filter: false,
                    customBodyRenderLite: (dataIndex) => {
                        const row = rows[dataIndex]
                        return (
                           <>
                           {row?.storeId?.costCenterId?.cost_center_number ?? ''} {row?.storeId?.costCenterId?.name ? '-' : ''} {row?.storeId?.name ?? '-'}
                           </>
                        )
                    },
                },
            },
            {
                name: 'action',
                label: 'Action',
                options: {
                    filter: false,
                    customBodyRenderLite: (dataIndex) => {
                        const row = rows[dataIndex]
                        return (
                            <Tooltip title="Remove from the cart">
                                <IconButton
                                    size="small"
                                    color="error"
                                    aria-label="Remove from the cart"
                                    onClick={() => setRemoveTarget(row)}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Tooltip>
                        )
                    },
                },
            },
        ],
        [rows]
    )

    return (
        <Container>
            <div className="breadcrumb">
                <Breadcrumb
                    routeSegments={[
                        { name: 'Pages' },
                        { name: 'Orders' },
                        { name: 'Cart' },
                    ]}
                />
            </div>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}{' '}
                    <Button size="small" onClick={fetchCarts}>
                        Retry
                    </Button>
                </Alert>
            ) : null}

            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ flex: 1, maxWidth: { md: 360 } }}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Select location"
                                value={selectedStore}
                                onChange={(e) => setSelectedStore(e.target.value)}
                            >
                                <MenuItem value="">All locations</MenuItem>
                                {storeOptions.map((store) => (
                                    <MenuItem key={store?._id} value={store?._id}>
                                        {formatStoreLabel(store)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        {selectedStore ? (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                >
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            bgcolor: 'primary.light',
                                            color: 'primary.dark',
                                            width: 44,
                                            height: 44,
                                        }}
                                    >
                                        <StorefrontRoundedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ lineHeight: 1.2 }}
                                        >
                                            {selectedStoreObj
                                                ? formatStoreLabel(selectedStoreObj)
                                                : 'Selected location'}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                            label={`${selectedStoreItemCount} ${
                                                selectedStoreItemCount === 1
                                                    ? 'item'
                                                    : 'items'
                                            } in cart`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Stack>

                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={checkoutLoading}
                                    disabled={selectedStoreItemCount === 0}
                                    onClick={handleOpenCheckoutConfirm}
                                    startIcon={<ShoppingCartCheckoutRoundedIcon />}
                                >
                                    Continue to Checkout
                                </LoadingButton>
                            </Stack>
                        ) : null}
                    </Stack>
                </CardContent>
            </Card>

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box overflow="auto">
                    <Box minWidth={600}>
                        <MUIDataTable
                            title="Cart Items"
                            data={rows}
                            columns={columns}
                            options={{
                                filterType: 'dropdown',
                                selectableRows: 'none',
                                draggableColumns: { enabled: true, transitionTime: 500 },
                                jumpToPage: true,
                                search: false,
                                responsive: 'standard',
                                download: false,
                                filter: false,
                                print: false,
                                pagination: true,
                                viewColumns: false,
                                elevation: 1,
                                rowsPerPage: 10,
                                rowsPerPageOptions: [10, 20, 40, 80, 100],
                                textLabels: {
                                    body: {
                                        noMatch: 'Your cart is empty.',
                                    },
                                },
                            }}
                        />
                    </Box>
                </Box>
            )}

            <Dialog
                open={Boolean(removeTarget)}
                onClose={(_, reason) => {
                    if (removeLoading) return
                    if (
                        reason === 'backdropClick' ||
                        reason === 'escapeKeyDown'
                    )
                        handleCloseRemoveConfirm()
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        borderTop: '4px solid',
                        borderTopColor: 'error.main',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Avatar
                            variant="rounded"
                            sx={{
                                bgcolor: 'error.light',
                                color: 'error.dark',
                                width: 48,
                                height: 48,
                            }}
                        >
                            <RemoveShoppingCartRoundedIcon />
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                            <Typography variant="h6" component="span">
                                Remove from cart?
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                This item will be removed from the cart. This
                                cannot be undone.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Avatar
                            src={removeTarget?.memberData?.photo}
                            alt={memberFullName(removeTarget?.memberData)}
                            sx={{ width: 44, height: 44 }}
                        />
                        <Box minWidth={0}>
                            <Typography variant="subtitle2" noWrap>
                                {memberFullName(removeTarget?.memberData)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                            >
                                {removeTarget?.storeId?.name ||
                                    removeTarget?.store?.name ||
                                    ''}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseRemoveConfirm}
                        disabled={removeLoading}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        loading={removeLoading}
                        onClick={executeRemove}
                        startIcon={<DeleteOutlineIcon />}
                    >
                        Remove
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={checkoutConfirmOpen}
                onClose={(_, reason) => {
                    if (checkoutLoading) return
                    if (
                        reason === 'backdropClick' ||
                        reason === 'escapeKeyDown'
                    )
                        handleCloseCheckoutConfirm()
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        borderTop: '4px solid',
                        borderTopColor: 'primary.main',
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Avatar
                            variant="rounded"
                            sx={{
                                bgcolor: 'primary.light',
                                color: 'primary.dark',
                                width: 48,
                                height: 48,
                            }}
                        >
                            <ShoppingCartCheckoutRoundedIcon />
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                            <Typography variant="h6" component="span">
                                Continue to checkout?
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                Please confirm the items and location before
                                continuing.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack
                        spacing={1.5}
                        sx={{
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <StorefrontRoundedIcon color="action" />
                            <Box minWidth={0}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Location
                                </Typography>
                                <Typography variant="subtitle2" noWrap>
                                    {selectedStoreObj
                                        ? formatStoreLabel(selectedStoreObj)
                                        : 'Selected location'}
                                </Typography>
                            </Box>
                        </Stack>
                        <Divider />
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography variant="body2" color="text.secondary">
                                Items in cart
                            </Typography>
                            <Chip
                                label={`${selectedStoreItemCount} ${
                                    selectedStoreItemCount === 1
                                        ? 'item'
                                        : 'items'
                                }`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseCheckoutConfirm}
                        disabled={checkoutLoading}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={checkoutLoading}
                        onClick={handleConfirmCheckout}
                        startIcon={<ShoppingCartCheckoutRoundedIcon />}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={toast.success ? 'success' : 'error'}
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    )
}

export default CartViewer
